import { useState, useEffect } from 'react';
import type { Note, NoteType } from '../../../types/note';
import { supabase } from '../../../lib/supabase';

interface NotesPanelProps {
  type: NoteType;
  entityId: string;
  entityName: string;
  onClose: () => void;
}

export default function NotesPanel({ type, entityId, entityName, onClose }: NotesPanelProps) {
  const [notes, setNotes] = useState<Note[]>([]);
  const [newNoteContent, setNewNoteContent] = useState('');
  const [editingNoteId, setEditingNoteId] = useState<string | null>(null);
  const [editContent, setEditContent] = useState('');
  const [isAdding, setIsAdding] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadNotes();
  }, [type, entityId]);

  const loadNotes = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('notes')
        .select('*')
        .eq('type', type)
        .eq('entity_id', entityId)
        .order('created_at', { ascending: false });

      if (error) throw error;

      // Mapear los datos de Supabase al formato Note
      const mappedNotes: Note[] = (data || []).map(n => ({
        id: n.id,
        type: n.type as NoteType,
        entityId: n.entity_id,
        content: n.content,
        date: n.created_at,
        author: 'admin@bnagenzia.com'
      }));

      setNotes(mappedNotes);
    } catch (error) {
      console.error('Error al cargar notas:', error);
      setNotes([]);
    } finally {
      setLoading(false);
    }
  };

  const handleAddNote = async () => {
    if (!newNoteContent.trim()) return;

    try {
      const { error } = await supabase
        .from('notes')
        .insert({
          type,
          entity_id: entityId,
          content: newNoteContent.trim()
        });

      if (error) throw error;

      setNewNoteContent('');
      setIsAdding(false);
      await loadNotes();
    } catch (error) {
      console.error('Error al añadir nota:', error);
      alert('Error al guardar la nota. Por favor, inténtalo de nuevo.');
    }
  };

  const handleEditNote = async (noteId: string) => {
    if (!editContent.trim()) return;

    try {
      const { error } = await supabase
        .from('notes')
        .update({ 
          content: editContent.trim(),
          updated_at: new Date().toISOString()
        })
        .eq('id', noteId);

      if (error) throw error;

      setEditingNoteId(null);
      setEditContent('');
      await loadNotes();
    } catch (error) {
      console.error('Error al editar nota:', error);
      alert('Error al actualizar la nota. Por favor, inténtalo de nuevo.');
    }
  };

  const handleDeleteNote = async (noteId: string) => {
    if (!confirm('¿Estás segura de que deseas eliminar esta nota?')) return;

    try {
      const { error } = await supabase
        .from('notes')
        .delete()
        .eq('id', noteId);

      if (error) throw error;

      await loadNotes();
    } catch (error) {
      console.error('Error al eliminar nota:', error);
      alert('Error al eliminar la nota. Por favor, inténtalo de nuevo.');
    }
  };

  const startEdit = (note: Note) => {
    setEditingNoteId(note.id);
    setEditContent(note.content);
  };

  const cancelEdit = () => {
    setEditingNoteId(null);
    setEditContent('');
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  };

  const getTypeLabel = () => {
    switch (type) {
      case 'candidato': return 'Candidato';
      case 'empresa': return 'Empresa';
      case 'proceso': return 'Proceso';
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-6">
      <div className="bg-white rounded-xl w-full max-w-3xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <div>
            <h2 className="text-lg font-medium text-gray-900">Notas Internas</h2>
            <p className="text-sm text-gray-500 mt-1">
              {getTypeLabel()}: <span className="font-medium text-gray-700">{entityName}</span>
            </p>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 transition-colors"
          >
            <i className="ri-close-line text-xl text-gray-500"></i>
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* Add Note Button */}
          {!isAdding && (
            <button
              onClick={() => setIsAdding(true)}
              className="w-full px-4 py-3 border-2 border-dashed border-gray-300 rounded-lg text-sm text-gray-600 hover:border-gray-400 hover:text-gray-700 transition-colors flex items-center justify-center gap-2 mb-6 whitespace-nowrap"
            >
              <i className="ri-add-line text-lg"></i>
              Añadir nueva nota
            </button>
          )}

          {/* Add Note Form */}
          {isAdding && (
            <div className="bg-gray-50 rounded-lg p-4 mb-6 border border-gray-200">
              <textarea
                value={newNoteContent}
                onChange={(e) => setNewNoteContent(e.target.value)}
                placeholder="Escribe tu nota aquí..."
                maxLength={500}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent resize-none"
                rows={4}
              />
              <div className="flex items-center justify-between mt-3">
                <span className="text-xs text-gray-500">
                  {newNoteContent.length}/500 caracteres
                </span>
                <div className="flex gap-2">
                  <button
                    onClick={() => {
                      setIsAdding(false);
                      setNewNoteContent('');
                    }}
                    className="px-3 py-1.5 text-sm text-gray-600 hover:text-gray-900 transition-colors whitespace-nowrap"
                  >
                    Cancelar
                  </button>
                  <button
                    onClick={handleAddNote}
                    disabled={!newNoteContent.trim()}
                    className="px-4 py-1.5 bg-gray-900 text-white text-sm rounded-lg hover:bg-gray-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                  >
                    Guardar nota
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Notes List */}
          {loading ? (
            <div className="text-center py-12">
              <i className="ri-loader-4-line text-4xl text-gray-300 mb-2 animate-spin"></i>
              <p className="text-gray-500 text-sm">Cargando notas...</p>
            </div>
          ) : notes.length === 0 ? (
            <div className="text-center py-12">
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-sticky-note-line text-2xl text-gray-400"></i>
              </div>
              <p className="text-sm text-gray-500">No hay notas registradas</p>
              <p className="text-xs text-gray-400 mt-1">Las notas que añadas aparecerán aquí</p>
            </div>
          ) : (
            <div className="space-y-4">
              {notes.map((note) => (
                <div key={note.id} className="bg-white border border-gray-200 rounded-lg p-4">
                  {editingNoteId === note.id ? (
                    <>
                      <textarea
                        value={editContent}
                        onChange={(e) => setEditContent(e.target.value)}
                        maxLength={500}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent resize-none mb-3"
                        rows={4}
                      />
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-gray-500">
                          {editContent.length}/500 caracteres
                        </span>
                        <div className="flex gap-2">
                          <button
                            onClick={cancelEdit}
                            className="px-3 py-1.5 text-sm text-gray-600 hover:text-gray-900 transition-colors whitespace-nowrap"
                          >
                            Cancelar
                          </button>
                          <button
                            onClick={() => handleEditNote(note.id)}
                            disabled={!editContent.trim()}
                            className="px-4 py-1.5 bg-gray-900 text-white text-sm rounded-lg hover:bg-gray-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                          >
                            Guardar
                          </button>
                        </div>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="flex items-start justify-between gap-4 mb-3">
                        <div className="flex-1">
                          <p className="text-sm text-gray-700 leading-relaxed">{note.content}</p>
                        </div>
                        <div className="flex gap-1">
                          <button
                            onClick={() => startEdit(note)}
                            className="w-7 h-7 flex items-center justify-center rounded hover:bg-gray-100 transition-colors"
                            title="Editar nota"
                          >
                            <i className="ri-edit-line text-sm text-gray-500"></i>
                          </button>
                          <button
                            onClick={() => handleDeleteNote(note.id)}
                            className="w-7 h-7 flex items-center justify-center rounded hover:bg-red-50 transition-colors"
                            title="Eliminar nota"
                          >
                            <i className="ri-delete-bin-line text-sm text-red-500"></i>
                          </button>
                        </div>
                      </div>
                      <div className="flex items-center gap-3 text-xs text-gray-500">
                        <span className="flex items-center gap-1">
                          <i className="ri-user-line"></i>
                          {note.author}
                        </span>
                        <span className="flex items-center gap-1">
                          <i className="ri-time-line"></i>
                          {formatDate(note.date)}
                        </span>
                      </div>
                    </>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}